import { FC, ReactNode, useState } from 'react';
import { Checkbox } from 'antd';

import { CompactCollapsible, SmallTooltip } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import type { WorkspaceResourceKind } from '@/shared/components/workspace-resources';

import { getNotificationChannelGridColumns, NOTIFICATION_GROUP_CONTENT_INLINE_INSET } from './layout';
import NotificationOption from './NotificationOption';

export type NotificationChannel = 'email' | 'inApp' | 'push' | 'slack';

export type NotificationChannelConfig = {
    description?: string;
    icon: ReactNode;
    key: NotificationChannel;
    label: string;
};

export type NotificationOptionConfig = {
    description: string;
    key: string;
    title: string;
};

export type NotificationGroupConfig = {
    key: string;
    options: NotificationOptionConfig[];
    resource: WorkspaceResourceKind;
    title: string;
};

interface NotificationGroupProps {
    channels: NotificationChannelConfig[];
    group: NotificationGroupConfig;
    isChannelEnabled: (workflowKey: string, channel: NotificationChannel) => boolean;
    onChannelChange: (workflowKey: string, channel: NotificationChannel, checked: boolean) => void;
}

const NotificationGroup: FC<NotificationGroupProps> = ({
    channels,
    group,
    isChannelEnabled,
    onChannelChange,
}): JSX.Element => {
    const [open, setOpen] = useState(false);

    const checkboxCellStyle = {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        minWidth: 0,
    } as const;

    return (
        <CompactCollapsible
            bordered
            contentStyle={{ padding: '0.5rem 0.75rem 0.125rem' }}
            headerStyle={{
                paddingInlineEnd: 0,
                paddingInlineStart: NOTIFICATION_GROUP_CONTENT_INLINE_INSET,
            }}
            icon={<WorkspaceResourceGlyph resource={group.resource} size='1rem' />}
            id={`settings-account-notifications-${group.key}`}
            label={group.title}
            open={open}
            onOpenChange={setOpen}
            sectionStyle={{ marginBottom: '0.5rem' }}
            headerExtra={
                <div
                    style={{
                        alignItems: 'center',
                        display: 'grid',
                        gridTemplateColumns: getNotificationChannelGridColumns(channels.length, false),
                    }}
                >
                    {channels.map((channel) => {
                        const checkedCount = group.options.filter((option) =>
                            isChannelEnabled(option.key, channel.key),
                        ).length;
                        const checked = checkedCount === group.options.length;
                        const indeterminate = checkedCount > 0 && checkedCount < group.options.length;

                        return (
                            <div key={channel.key} style={checkboxCellStyle}>
                                <SmallTooltip
                                    title={`${checked ? 'Disable' : 'Enable'} ${channel.label} for ${group.title}`}
                                    placement='top'
                                    padding='0 0.25rem'
                                >
                                    <Checkbox
                                        checked={checked}
                                        indeterminate={indeterminate}
                                        onChange={(event) => {
                                            group.options.forEach((option) =>
                                                onChannelChange(option.key, channel.key, event.target.checked),
                                            );
                                            toast(`${group.title} (${channel.label})`, {
                                                description: event.target.checked ? 'On' : 'Off',
                                            });
                                        }}
                                    />
                                </SmallTooltip>
                            </div>
                        );
                    })}
                </div>
            }
        >
            {group.options.map((option) => (
                <NotificationOption
                    key={option.key}
                    channels={channels}
                    option={option}
                    isChannelEnabled={isChannelEnabled}
                    onChannelChange={onChannelChange}
                />
            ))}
        </CompactCollapsible>
    );
};

export default NotificationGroup;
